import { Component, type ErrorInfo, type ReactNode } from 'react';

// The port of app/global-error.tsx: the app-shell error boundary the Next root layout
// wrapped every page in. It renders what `next/error` renders for a status code the App
// Router cannot know (statusCode 0), so a page component that throws leaves the same
// message behind rather than a blank document.
//
// The Next version also reports to Sentry; the Rails app has no Sentry SDK installed, so
// the error is logged and nothing is sent.
export class GlobalErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const host =
      typeof window === 'undefined' ? '' : ` while loading ${window.location.hostname}`;

    return (
      <div
        style={{
          fontFamily:
            'system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji"',
          height: '100vh',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div>
          <h1
            style={{
              display: 'inline-block',
              margin: '0 20px 0 0',
              padding: '0 23px 0 0',
              fontSize: 24,
              fontWeight: 500,
              verticalAlign: 'top',
              lineHeight: '49px',
              borderRight: '1px solid rgba(0, 0, 0, 0.3)',
            }}
          >
            0
          </h1>
          <div style={{ display: 'inline-block' }}>
            <h2
              style={{
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '49px',
                margin: 0,
              }}
            >
              {`Application error: a client-side exception has occurred${host} (see the browser console for more information).`}
            </h2>
          </div>
        </div>
      </div>
    );
  }
}
